import type { Group, Reference } from '@medplum/fhirtypes';

import { getGroupFacilitators } from '@unify/fhir-model/resources/group';
import type { DisplayUserRole } from '@/lib/displayUserRole';

/**
 * Group setup admins: practice owner/admin/assistant and Unify admin/assistant.
 * Accepts display roles (`App.tsx` shell) or raw Medplum role slugs.
 */
const GROUP_ADMIN_ROLES = new Set([
  'Practice Owner',
  'Practice Admin',
  'Practice Assistant',
  'Unify Admin',
  'Unify Assistant',
  'practice-owner',
  'practice-admin',
  'practice-assistant',
  'unify-admin',
  'unify-assistant',
]);

export function isPracticeGroupAdmin(role: DisplayUserRole | string | undefined): boolean {
  return !!role && GROUP_ADMIN_ROLES.has(role);
}

export function isGroupFacilitator(
  group: Group,
  practitionerRef: string | undefined,
): boolean {
  if (!practitionerRef) return false;
  const normalized = practitionerRef.startsWith('Practitioner/')
    ? practitionerRef
    : `Practitioner/${practitionerRef}`;
  return getGroupFacilitators(group).some((r: Reference) => r.reference === normalized);
}

/** Admins or facilitators of this cohort can open Group detail. */
export function canViewGroupDetail(
  role: DisplayUserRole | string | undefined,
  group: Group,
  practitionerRef: string | undefined,
): boolean {
  return isPracticeGroupAdmin(role) || isGroupFacilitator(group, practitionerRef);
}

/** Only practice admins may edit schedule/setup, activate, or archive. */
export function canEditGroupSetup(role: DisplayUserRole | string | undefined): boolean {
  return isPracticeGroupAdmin(role);
}

/** Roster + sessions: admins and facilitators. */
export function canManageGroupRoster(
  role: DisplayUserRole | string | undefined,
  group: Group,
  practitionerRef: string | undefined,
): boolean {
  return canViewGroupDetail(role, group, practitionerRef);
}
